import { Suspense, lazy, useEffect, useState } from 'react';
import { cn } from '@/lib/utils';

const ParticleCanvas = lazy(() =>
  import('./NeuNeuParticleAvatar').then(m => ({ default: m.NeuNeuParticleCanvas })),
);

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(() =>
    typeof window !== 'undefined'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  );

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return reduced;
}

function StaticFallback({ className }: { className?: string }) {
  return (
    <div
      aria-hidden
      className={cn(
        'h-full w-full rounded-full bg-linear-to-br from-brand-400 to-brand-600 shadow-[0_0_16px_hsl(187_85%_43%/0.45)]',
        className,
      )}
    />
  );
}

function LoadingFallback({ className }: { className?: string }) {
  return (
    <div
      aria-hidden
      className={cn('h-full w-full animate-pulse rounded-full bg-brand-500/20', className)}
    />
  );
}

interface NeuNeuAvatarLayerProps {
  className?: string;
}

/** NeuNeu 3D particle layer — mount once, positioned by parent. */
export function NeuNeuAvatarLayer({ className }: NeuNeuAvatarLayerProps) {
  const reducedMotion = usePrefersReducedMotion();

  if (reducedMotion) {
    return <StaticFallback className={className} />;
  }

  return (
    <Suspense fallback={<LoadingFallback className={className} />}>
      <ParticleCanvas className={className} />
    </Suspense>
  );
}
